import { useEffect, useRef } from "react";
import LiftCancellationAction from "../LiftCancellationAction";

const brl = value => (Number(value) || 0).toLocaleString("pt-BR", {
  style:"currency",
  currency:"BRL",
  maximumFractionDigits:0,
});

function formatDate(value) {
  if (!value) return "Não informada";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, year, month, day] = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Não informada"
    : date.toLocaleDateString("pt-BR");
}

function firstValue(...values) {
  return values.find(value => value !== null && value !== undefined && String(value).trim() !== "");
}

export default function SaleDetailsPanel({
  sale, semVinculo = false, users = [], stores = [], onClose,
}) {
  const closeRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    const onKeyDown = event => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus?.();
    };
  }, [onClose]);

  if (!sale) return null;
  const crossStore = sale.crossStore === true;
  const record = sale.record || {};
  const client = sale.client || {};
  const name = firstValue(sale.name, client.name, client.nome, record.name, record.nome, "Não identificado");
  const consultant = users.find(user => Number(user.id) === Number(sale.consultantId))?.name || "Não informado";
  const store = stores.find(item => Number(item.id) === Number(sale.storeId))?.name || "Não informada";
  const orderNumber = firstValue(
    sale.liftPedidoId,
    record.liftPedidoId,
    record.pedidoNumero,
    record.numeroPedido,
    record.orderNumber,
    sale.pedidoNumero,
    sale.numeroPedido,
    sale.orderNumber,
  ) || "Não informado";
  const fields = crossStore
    ? [
      ["Cliente", "Venda em outra loja"],
      ["Valor", brl(sale.value)],
      ["Data", "Não exibida"],
      ["Consultor", "Não exibido"],
      ["Loja", "Outra loja"],
      ["Número do pedido", "Não exibido"],
      ["Origem", "Não exibida"],
    ]
    : [
      ["Cliente", name],
      ["Valor", brl(sale.value)],
      ["Data", formatDate(sale.saleDate)],
      ["Consultor", consultant],
      ["Loja", store],
      ["Número do pedido", orderNumber],
      ["Origem", firstValue(sale.origin, record.origin, client.origin, "Não informada")],
    ];

  return (
    <div role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose?.();
    }} style={{
      position:"fixed", inset:0, zIndex:500, background:"rgba(15,23,42,.5)",
      display:"grid", placeItems:"center", padding:16,
    }}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="nu-sale-details-title"
        style={{
          width:"min(100%, 480px)", maxHeight:"85vh", overflowY:"auto",
          background:"#fff", borderRadius:12, padding:20, color:"#1C1917",
          boxShadow:"0 12px 44px rgba(15,23,42,.25)",
        }}
      >
        <header style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:16 }}>
          <h2 id="nu-sale-details-title" style={{ margin:0, fontSize:20 }}>Detalhes da venda</h2>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Fechar detalhes da venda" style={{
            border:"1px solid #E7E5E4", borderRadius:8, background:"#fff", color:"#44403C",
            width:36, height:36, cursor:"pointer", fontSize:20,
          }}>×</button>
        </header>
        {semVinculo && (
          <div style={{
            border:"1px solid #FCD34D", background:"#FFFBEB", color:"#92400E",
            borderRadius:8, padding:"9px 11px", marginBottom:14, fontSize:13, fontWeight:800,
          }}>Venda sem vínculo</div>
        )}
        <dl style={{ margin:0, display:"grid", gridTemplateColumns:"minmax(120px, .8fr) minmax(0, 1.2fr)", gap:"12px 16px" }}>
          {fields.map(([label, value]) => (
            <div key={label} style={{ display:"contents" }}>
              <dt style={{ color:"#78716C", fontSize:13 }}>{label}</dt>
              <dd style={{ margin:0, fontSize:14, fontWeight:600, overflowWrap:"anywhere" }}>{value}</dd>
            </div>
          ))}
        </dl>
        <LiftCancellationAction sale={sale} onDone={onClose}/>
      </section>
    </div>
  );
}